import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatDistanceToNow } from 'date-fns'
import { ar, fr } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, Filter } from 'lucide-react'
import type { DetailedOrder } from '@/hooks/useOrderStats'
import { formatPrice } from '@/lib/utils'

interface OrdersTableProps {
  orders: DetailedOrder[]
  pageSize: number
  isLoading?: boolean
}

export function OrdersTable({
  orders,
  pageSize,
  isLoading,
}: OrdersTableProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? ar : fr

  const [selectedMenuItem, setSelectedMenuItem] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)

  const hasActiveFilters = selectedMenuItem !== 'all' || selectedStatus !== 'all'

  // Extract unique menu items from all orders
  const menuItemsList = useMemo(() => {
    const itemsSet = new Set<string>()
    orders.forEach((order) => {
      order.items.forEach((item) => itemsSet.add(item.name))
    })
    return Array.from(itemsSet).sort()
  }, [orders])

  // Filter orders
  const filteredOrders = useMemo(() => {
    let filtered = orders

    if (selectedMenuItem !== 'all') {
      filtered = filtered.filter((order) =>
        order.items.some((item) => item.name === selectedMenuItem)
      )
    }

    if (selectedStatus !== 'all') {
      filtered = filtered.filter((order) => order.status === selectedStatus)
    }

    return filtered
  }, [orders, selectedMenuItem, selectedStatus])

  // Client-side pagination on filtered results
  const displayOrders = useMemo(() => {
    const offset = (currentPage - 1) * pageSize
    return filteredOrders.slice(offset, offset + pageSize)
  }, [filteredOrders, currentPage, pageSize])

  const totalCount = filteredOrders.length
  const totalPages = Math.ceil(totalCount / pageSize)

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500'
      case 'in_progress':
        return 'bg-blue-500'
      case 'ready':
        return 'bg-green-500'
      case 'completed':
        return 'bg-gray-500'
      case 'cancelled':
        return 'bg-red-500'
      default:
        return 'bg-gray-400'
    }
  }

  const resetFilters = () => {
    setSelectedMenuItem('all')
    setSelectedStatus('all')
    setCurrentPage(1)
  }

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value)
    setCurrentPage(1)
  }

  const handleMenuItemChange = (value: string) => {
    setSelectedMenuItem(value)
    setCurrentPage(1)
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.orderStatistics')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        </CardContent>
      </Card>
    )
  }

  if (orders.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.orderStatistics')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t('analytics.noOrdersForPeriod')}
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{t('analytics.orderStatistics')}</CardTitle>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {hasActiveFilters
                ? `${filteredOrders.length} / ${orders.length} ${t('order.items')}`
                : `${orders.length} ${t('order.items')}`}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">
              {t('analytics.filterByMenuItem')}
            </label>
            <Select value={selectedMenuItem} onValueChange={handleMenuItemChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('analytics.allMenuItems')}</SelectItem>
                {menuItemsList.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1">
            <label className="text-sm font-medium mb-2 block">
              {t('analytics.filterByStatus')}
            </label>
            <Select value={selectedStatus} onValueChange={handleStatusChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('analytics.allStatuses')}</SelectItem>
                <SelectItem value="pending">{t('order.statuses.pending')}</SelectItem>
                <SelectItem value="in_progress">{t('order.statuses.in_progress')}</SelectItem>
                <SelectItem value="ready">{t('order.statuses.ready')}</SelectItem>
                <SelectItem value="completed">{t('order.statuses.completed')}</SelectItem>
                <SelectItem value="cancelled">{t('order.statuses.cancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters && (
            <div className="flex items-end">
              <Button variant="outline" size="sm" onClick={resetFilters}>
                {t('analytics.resetFilters')}
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('table.tableNumber')}</TableHead>
                <TableHead>{t('analytics.server')}</TableHead>
                <TableHead>{t('menu.name')}</TableHead>
                <TableHead>{t('order.status')}</TableHead>
                <TableHead>{t('order.total')}</TableHead>
                <TableHead>{t('analytics.orderTime')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    {t('analytics.noOrdersMatchFilter')}
                  </TableCell>
                </TableRow>
              ) : (
                displayOrders.map((order) => (
                  <TableRow key={order.id}>
                    <TableCell className="font-medium">
                      {t('table.table')} #{order.tableNumber}
                    </TableCell>
                    <TableCell className="text-sm">
                      {order.serverName || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {order.items.map((item, index) => (
                          <div key={index} className={`text-sm flex items-center gap-2 ${item.isCancelled ? 'text-muted-foreground' : ''}`}>
                            <div className={item.isCancelled ? 'line-through' : ''}>
                              <span className="font-medium">{item.quantity}x</span> {item.name}
                              <span className="text-muted-foreground ml-2">
                                ({formatPrice(item.price * item.quantity)} DH)
                              </span>
                            </div>
                            {item.isCancelled && (
                              <Badge className="bg-red-500 text-[10px] h-4 px-1">
                                {t('order.statuses.cancelled')}
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Badge variant="default" className={getStatusColor(order.status)}>
                          {t(`order.statuses.${order.status}`)}
                        </Badge>
                        {order.status === 'cancelled' && order.cancellationConfirmedByName && (
                          <div className="text-xs text-muted-foreground">
                            {t('order.confirmedBy')}: {order.cancellationConfirmedByName}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-bold">{formatPrice(order.totalAmount)} DH</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(order.createdAt), {
                        addSuffix: true,
                        locale,
                      })}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t gap-3">
            <p className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left order-2 sm:order-1">
              {t('analytics.showingOrders', {
                from: (currentPage - 1) * pageSize + 1,
                to: Math.min(currentPage * pageSize, totalCount),
                total: totalCount,
              })}
            </p>
            <div className="flex gap-2 order-1 sm:order-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="h-9"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{t('common.back')}</span>
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(totalPages <= 3 ? totalPages : 3, totalPages) }, (_, i) => {
                  let pageNum
                  if (totalPages <= 3) {
                    pageNum = i + 1
                  } else if (currentPage <= 2) {
                    pageNum = i + 1
                  } else if (currentPage >= totalPages - 1) {
                    pageNum = totalPages - 2 + i
                  } else {
                    pageNum = currentPage - 1 + i
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                      className="w-9 h-9 p-0"
                    >
                      {pageNum}
                    </Button>
                  )
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="h-9"
              >
                <span className="hidden sm:inline">{t('common.next')}</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
